import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "./axiosConfig";
import "./UserManagement.css"; // Import new CSS file
import Modal from "./components/Modal";

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [searchString, setSearchString] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(10);  
  const [selectedUser, setSelectedUser] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [groupDropdown, setGroupDropdown] = useState([]);
  const [userDropdown, setUserDropdown] = useState([]);

  const [isCreateRecordModalOpen, setIsCreateRecordModalOpen] = useState(false);

  const navigate = useNavigate();    
  const token = localStorage.getItem("accessToken");



  useEffect(() => {
    const fetchUsersDropdown = async () => {
      try {
          const response = await api.get("/user/dropdown", { 
              headers: { Authorization: token }
          });
          setUserDropdown(response.data || []);
      } catch (error) {
          console.error("Error fetching users:", error);
      }
    };
    fetchUsersDropdown();
  }, [isCreateRecordModalOpen, token]);

  const fetchUsers = useCallback(
    async (page) => {
      try {
        const response = await api.post(
          "/user/list",
          {
            searchString: searchString,
            skip: (page - 1) * pageSize,
            take: pageSize,
          },
          { headers: { Authorization: token } }
        );

        const data = response.data.data;
        if (data) {
          setUsers(data);
          setTotalPages(response.data.pagination.totalPages);
          setCurrentPage(response.data.pagination.currentPage);
        } else {
          console.error("Failed to fetch users:", data.message);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    },
    [searchString, pageSize, token]
  );
  
  useEffect(() => {
    fetchUsers(currentPage);
  }, [currentPage, searchString, fetchUsers]);

  const handleSearchChange = (e) => {
    setSearchString(e.target.value);
    setCurrentPage(1);
  };

  const getGroupList = async () => {
    try {
      const response = await api.get("/group/dropdown", { 
          headers: { Authorization: token }
      });
      setGroupDropdown(response.data);  
    } catch (error) {
        console.error("Error fetching users:", error);
    }
  };

  const handleEdit = async (userData) => {
      try {
          await api.get(`/user/${userData.id}`, {
          headers: { Authorization: token },
        });
        setSelectedUser(userData)
        getGroupList();
        setIsEditModalOpen(true);
        console.log(groupDropdown);
      } catch (error) {
        console.error("Error fetching user:", error);
        alert("Failed to load user data. Please try again.");
      };
  }

  const handleDelete = async (userData) => {
      try {
        await api.patch(`/user/${userData.id}/soft-delete`, {
          headers: { Authorization: token },
        });
        setIsDeleteModalOpen(false);
        fetchUsers(currentPage);
      } catch (error) {
        console.error("Error fetching user:", error);
        alert("Failed to load user data. Please try again.");
      };
  };

  const handleSave = async (userData) => {
      try {
        await api.patch(`/user/${userData.id}`, userData, {
        headers: { Authorization: token },
        });
        setIsEditModalOpen(false);
        fetchUsers(currentPage);
      } catch (error) {
        console.error("Error updating user:", error);
      }
  };

  const handleCreateRecord = async (data) => {
    try {
      const localDate = new Date(data.clockInTime);
      const payload = {
        ...data,
        clockInTime: localDate,
      };
      await api.post("/manual-clock-in", payload, {
        headers: {Authorization: token }
      });

      setIsCreateRecordModalOpen(false);
    } catch (error) {
      console.error("Error create clock in record:", error);
    }
  };

  const handleBack = () => {
      navigate("/admin");
  };

  const handleCloseEdit = () => {
      setIsEditModalOpen(false);
      setSelectedUser(null);
  };
    
  const handleCloseDelete = () => {
      setIsDeleteModalOpen(false);
      setSelectedUser(null);
  };

  const handleDeleteClick = (user) => {
      setSelectedUser(user);
      setIsDeleteModalOpen(true);
  };

  return (
    <div className="user-management-container">
      <button className="back-button" onClick={handleBack}>
        Back
      </button>
      <div className="header">
        <h2 className="user-management-title">User Management</h2>
      </div>

      <div className="search-bar">
        <input
          type="text"
          className="search-input"
          placeholder="Search users..."
          value={searchString}
          onChange={handleSearchChange}
        />

        <button className="user-list-button" 
        style={{ marginLeft: "1.25rem"}}
        onClick={() => {setIsCreateRecordModalOpen(true) 
        console.log(userDropdown)}}>
          Add Clock In Record
        </button>
      </div>

      <table className="user-table">
        <thead>
          <tr>
            <th>Username</th>
            <th>Full Name</th>
            <th>Role</th>
            <th>Status</th>
            <th>Groups</th>
            <th style={{minWidth: "10rem"}}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.id}>
              <td>{user.username}</td>
              <td>{user.fullname}</td>
              <td>{user.role}</td>
              <td>{user.is_deleted ? "Inactive" : "Active"}</td>
              <td>{user.group_details.name ?? "-"}</td>
              <td>
                <div>
                  <button className="edit-button" onClick={() => handleEdit(user)}>
                    Edit
                  </button>
                  <button className="delete-button" onClick={() => handleDeleteClick(user)}>
                    Delete
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="pagination">
        <button
          className="pagination-button"
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        <span>Page {currentPage} of {totalPages}</span>
        <button
          className="pagination-button"
          onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>

    <Modal
      isOpen={isEditModalOpen}
      onClose={handleCloseEdit}
      title="Edit User"
      fields={[
        { name: "id", label: "ID", value: selectedUser?.id, hidden: true },
        { name: "username", label: "Username", value: selectedUser?.username },
        { name: "fullname", label: "Full Name", value: selectedUser?.fullname },
        { name: "role", label: "Role", value: selectedUser?.role, type: "dropdown", 
          dropdownOptions: [ 
            { id: "employee", name: "employee"}, 
            { id: "admin", name: "admin"}, 
        ]},
        { name: "group_id", label: "Group", value: selectedUser?.group_details?.id, type: "dropdown",
          dropdownOptions: groupDropdown },
      ]}
      onSubmit={(data) => handleSave(data)}
      mode="edit"
    />

    <Modal
      isOpen={isDeleteModalOpen}
      onClose={handleCloseDelete}
      title="Delete User"
      message="Are you sure to delete this User"
      fields={[
        { name: "id", label: "ID", value: selectedUser?.id, hidden: true },
        { name: "username", label: "Username", value: selectedUser?.username },
        { name: "is_deleted", label: "IsDeleted", value: selectedUser?.is_deleted, hidden: true }
      ]}
      onSubmit={(data) => handleDelete(data)}
      mode="delete"
      />

    <Modal
      isOpen={isCreateRecordModalOpen}
      onClose={() => setIsCreateRecordModalOpen(false)}
      title="Create Clock Record"
      fields={[
        { name: "userId", label: "User", type: "dropdown", require:true, 
          dropdownOptions: userDropdown.map((e) => ({
            id: e.id,
            name: e.fullname
          }))},
        { name: "remarks", label: "Remark"},
        { name: "clockInTime", label: "Clock In Time", type: "datetime-local", required: true }
      ]}
      onSubmit={(data) => handleCreateRecord(data)}
      mode="create"
      />
    </div>
  );
};

export default UserManagement;
